import { mapState } from 'vuex';
import ReportNotify from '@/components/reports/notify/ReportNotify.vue';

const defaultFilterValues = {
  clientName: '',
  reportStatuses: [],
  reportMaterialGroups: [],
  fromDatePeriod: null,
  toDatePeriod: null,
  clientManagers: [],
  clientCategories: [],
  clientStatuses: [],
  reportSubCode: [],
  clientOnlyMine: []
};

export default {
  name: 'ReportList',
  props: {
    data: Array,
    clientApiId: String,
    loadData: {
      type: Boolean,
      default: true
    },
    filtersDisabled: {
      type: Boolean,
      default: false
    },
    pagingData: Object,
    colOnlyFilters: {
      type: Boolean,
      default: false
    },
    displayReload: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      isMounted: false,
      loading: false,
      items: null,
      filterValues: this.BasicHelper.cloneObject(defaultFilterValues),
      currentReq: null,
      tableSorting: { item: 'date_period_id', direction: 'desc' },
      exportCols: null,
      paging: {
        limit: 10,
        total: 0,
        page: 1,
        pages: 1
      }
    }
  },
  components: {
    ReportNotify
  },
  computed: {
    ...mapState(['config']),
    tableHeaders () {
      const headers = [
        { key: 'report_config_id', label: this.$t('report.title'), format: 'formatFromConfig', configKey: 'report_configs', configParam: 'name' },
        { key: 'date_period_id', label: this.$t('general.datePeriod.title'), format: 'formatDatePeriod', sortable: !this.filtersDisabled },
        { key: 'updated_at', label: this.$t('general.updated_at'), type: 'datetime', sortable: !this.filtersDisabled }
      ];
      if (this.canViewAll) {
        headers.push({ key: 'status_id', label: this.$t('report.status'), format: 'formatFromConfig', configKey: 'client_report_statuses', configParam: 'name', sortable: !this.filtersDisabled });
      }
      headers.push({ key: 'bills', label: this.$t('report.bills'), type: 'actionlinks', emit: 'downloadReportBill', actionNameParam: 'bill_number', ifconfig: { param: 'file', value: null, notcheck: true } })
      headers.push({ key: 'summary.total', label: this.$t('general.total'), format: 'formatMeasureTypeValues' });
      headers.push({ key: 'summary.cost', label: this.$t('general.currency'), format: 'formatDecimal', precision: 2 });
      headers.push({
        key: 'actions',
        label: '',
        actions: [
          { label: this.$t('menu.reports.view'), labelFunc: this.getViewLabel, classFunc: this.getViewClass, emit: 'editReport', bicon: (this.canViewAll ? 'list' : null) }
        ]
      });
      if (this.canViewAll) {
        headers.unshift({ key: 'summary', label: '', format: 'formatReportModificationIcons', class: 'text-right' });
      }
      if (!this.clientId) {
        headers.unshift({ key: 'client.name', label: this.$t('client.name'), type: 'routelink', routeTo: 'menu.client.edit', params: { clientApiId: 'client.api_id' }, filterKey: 'clientName', sortable: true });
      }
      return headers;
    },
    tableFilterConfig () {
      if (this.filtersDisabled) {
        return null;
      }
      const filters = { };
      filters.reportSubCode = { type: 'checkbox', label: this.$t('client.filters.reportSubCode'), options: this.reportNameOptions, colOnlyFilter: false };
      if (this.canViewAll) {
        filters.reportStatuses = { type: 'checkbox', colFilterType: 'select', label: this.$t('report.status'), options: this.reportStatusOptions, class: 'col-sm-8', colOnlyFilter: this.colOnlyFilters };
      }
      filters.fromDatePeriod = { type: 'date-period', label: this.$t('general.filters.date-period-from'), colOnlyFilter: false }
      filters.toDatePeriod = { type: 'date-period', label: this.$t('general.filters.date-period-to'), colOnlyFilter: false }
      if (!this.clientId) {
        filters.clientName = { type: 'text', label: this.$t('client.name'), colOnlyFilter: this.colOnlyFilters };
        filters.clientCategories = { type: 'checkbox', label: this.$t('client.category'), options: this.clientCategoryOptions, colOnlyFilter: this.colOnlyFilters };
        filters.clientManagers = { type: 'checkbox', label: this.$t('client.manager'), options: this.managerOptions, class: 'col-md-4', colOnlyFilter: false, valueField: 'api_id' };
        filters.clientStatuses = { type: 'checkbox', label: this.$t('client.status.title'), options: this.clientStatusOptions, class: 'col-md-2', colOnlyFilter: this.colOnlyFilters };
        filters.clientOnlyMine = { type: 'checkbox', label: ' ', options: this.onlyMineOptions, class: 'col-md-2', colOnlyFilter: this.colOnlyFilters };
      }

      return filters;
    },
    clientId () {
      if (this.clientApiId) {
        return this.clientApiId;
      }
      if (!this.canViewAll) {
        return this.BasicHelper.getUserClientApiId();
      }
      return null;
    },
    filterActions () {
      if (this.filtersDisabled) {
        return {};
      }
      const actions = { filters: false, reload: false, download: true, extra: [] };
      if (!this.canViewAll || this.clientId) {
        actions.download = false;
      }
      if (this.canViewAll && !this.clientId) {
        actions.extra.push({ title: this.$t('menu.reports.notify.title'), class: 'btn-secondary', event: 'openReportNotifyFrm', icon: 'envelope-fill' });
      }
      return actions;
    },
    clientCategoryOptions () {
      return this.$store.getters.getConfigOptions('client_categories', this.$i18n.locale);
    },
    clientStatusOptions () {
      return [
        { code: 0, text: this.$t('client.status.active') },
        { code: 1, text: this.$t('client.status.blocked') }
      ]
    },
    onlyMineOptions () {
      return [
        { code: 1, text: this.$t('client.onlyMine') }
      ];
    },
    managerOptions () {
      const options = this.$store.getters.getConfigOptions('managers');
      return options;
    },
    reportNameOptions () {
      return this.ZPFormatHelper.getReportNameOptions(false, false);
    },
    reportStatusOptions () {
      return this.$store.getters.getConfigOptions('client_report_statuses', this.$i18n.locale);
    },
    requestPath () {
      if (this.clientId) {
        return 'client/single/' + this.clientId + '/reports';
      }
      return 'report/list';
    },
    canViewAll () {
      return this.ApiRequest.userHasPermission('view-clients');
    },
    getTableClass () {
      if (this.filtersDisabled) {
        return 'table-bottom-border';
      }
      return '';
    }
  },
  methods: {
    editReport (report, newtab) {
      let clientId = this.clientId;
      if (report.client && report.client.api_id) {
        clientId = report.client.api_id;
      }
      const reportCode = this.$store.getters.getParamFromConfig('report_configs', report.report_config_id, 'code');
      if (newtab) {
        const routeData = this.$router.resolve({ name: 'menu.client.report', params: { clientApiId: clientId, reportCode: reportCode, datePeriodCode: report.date_period_id } });
        window.open(routeData.href, '_blank');
      } else {
        this.$router.push('/client/' + clientId + '/report/' + reportCode + '/' + report.date_period_id);
      }
    },
    reloadTable () {
      this.BasicHelper.updateFilterValues(this.$route.name, this.filterValues);
      this.changePage(this.paging.page);
    },
    changePage (pageNr) {
      if (this.currentReq) {
        this.currentReq.cancel('changed search query');
      }
      this.paging.page = pageNr;
      this.loading = true;
      this.$forceUpdate();
      let path = this.getRequestPath(false, pageNr);
      this.paging.page = pageNr;
      const self = this;
      let reqMode = this.ApiRequest.REQUEST_POST;
      if (this.clientId) {
        reqMode = this.ApiRequest.REQUEST_GET;
        path = this.ApiRequest.addGetParamsToPath(path, this.$refs.reportstable.getFilterValues(), true)
      }
      this.currentReq = this.ApiRequest.request(path, reqMode, this.$refs.reportstable.getFilterValues(), function (response) {
        if (response.data) {
          if (response.data.table) {
            self.items = response.data.table;
            const colConfig = [];
            response.data.columns.forEach(el => {
              colConfig.push({ key: el.key, name: self.$t(el.key), group: el.group });
            });
            self.exportCols = colConfig;
          } else {
            self.items = response.data;
          }
          self.paging.limit = response.paging.limit;
          self.paging.total = response.paging.total;
          self.paging.page = response.paging.page;
          self.paging.pages = response.paging.pages;
          self.loading = false;
        }
        self.$forceUpdate();
      })
    },
    getRequestPath (download, pageNr) {
      let path = this.requestPath;
      if (!download) {
        path += '?perPage=' + this.paging.limit + '&page=' + pageNr;
      } else {
        path += '?download=1';
      }
      let filterVals = {};
      if (!this.filtersDisabled) {
        filterVals = this.$refs.reportstable.getFilterValues();
        if (this.tableSorting.item) {
          path += '&order=' + this.tableSorting.item + ',' + this.tableSorting.direction;
        }
      }
      this.BasicHelper.updateFilterValues(this.$route.name, filterVals);
      return path;
    },
    download (html, exportCols) {
      const self = this;
      const path = this.getRequestPath(true, 1);
      this.$refs.reportstable.setDownloading(1);
      const reqData = this.$refs.reportstable.getFilterValues();
      if (exportCols) {
        reqData.cols = exportCols;
      }
      this.ApiRequest.request(path, this.ApiRequest.REQUEST_DOWNLOAD, reqData, function (response) {
        self.$refs.reportstable.setDownloading(0);
      })
    },
    updateFilter (key, value) {
      if (this.filterValues[key] !== value) {
        this.filterValues[key] = this.BasicHelper.cloneObject(value);
        this.reloadTable();
      }
    },
    clearFilters () {
      this.filterValues = this.BasicHelper.cloneObject(defaultFilterValues);
      this.reloadTable();
    },
    updateSort (item, desc) {
      this.tableSorting.item = item;
      if (desc) {
        this.tableSorting.direction = 'DESC';
      } else {
        this.tableSorting.direction = 'ASC';
      }
      this.changePage(this.paging.page);
    },
    downloadReportBill (rowIdx, billIdx) {
      this.items[rowIdx].bills[billIdx].loading = true;
      this.$refs.reportstable.$forceUpdate();
      const self = this;
      this.ApiRequest.request('client-bill/' + this.items[rowIdx].bills[billIdx].id, this.ApiRequest.REQUEST_DOWNLOAD, [], function () {
        self.items[rowIdx].bills[billIdx].loading = false;
        self.$refs.reportstable.$forceUpdate();
      });
    },
    getViewLabel (el) {
      const statusCode = this.$store.getters.getParamFromConfigByFilter('client_report_statuses', 'id', el.status_id, 'code');
      if (!this.canViewAll && statusCode === 'EMPTY') {
        return this.$t('menu.reports.fill');
      } else if (!this.canViewAll && (statusCode === 'REJECTED')) {
        return this.$t('menu.reports.edit');
      }
      return this.$t('menu.reports.view');
    },
    getViewClass (el) {
      const statusCode = this.$store.getters.getParamFromConfigByFilter('client_report_statuses', 'id', el.status_id, 'code');
      if (!this.canViewAll && (statusCode === 'EMPTY' || statusCode === 'REJECTED')) {
        if (el.date_period_id < this.config.currentDatePeriod.id) {
          return 'btn-error';
        } else {
          return 'btn-primary-border';
        }
      }
      return 'btn-secondary';
    },
    openReportNotifyFrm () {
      this.$refs.reportNotify.show(this.filterValues);
    }
  },
  mounted () {
    this.filterValues = this.BasicHelper.fillFilterValues(this.$route.name, this.filterValues, this.$session);
    this.isMounted = true;
    if (!this.loadData && this.data) {
      this.items = this.data;
      if (this.pagingData) {
        this.paging = this.pagingData;
      }
    }
  }
}
